import * as constants from '../constants/constants';
import { axiosCaller } from '../utils/apiCaller';
import { wrap } from './generalActions';

/**
 * Fetch the most recent notifications for the current user. The bell dropdown
 * defaults to 8 items; the archive page asks for 50. Failures fall back
 * silently — the bell is a low-stakes UI element and shouldn't block the page.
 */
export const fetchNotifications = (limit: number = 8, filters: { unread_only?: boolean; starred?: boolean; category?: string } = {}) => wrap(async (dispatch: any) => {
    dispatch({ type: constants.NOTIFICATIONS_SET_LOADING, payload: true });
    try {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        if (filters.unread_only) params.set('unread_only', '1');
        if (filters.starred) params.set('starred', '1');
        if (filters.category) params.set('category', filters.category);

        const res: any = await axiosCaller('GET', `notifications?${params.toString()}`);
        const payload = res?.data || {};
        dispatch({
            type: constants.NOTIFICATIONS_SET_ITEMS,
            payload: {
                items: payload.data || [],
                unreadCount: typeof payload.unread_count === 'number' ? payload.unread_count : undefined,
            },
        });
    } catch (e) {
        // Swallow — bell UI tolerates transient API failures.
    } finally {
        dispatch({ type: constants.NOTIFICATIONS_SET_LOADING, payload: false });
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Fetch notifications whose source is actively muted (for the Muted tab on
 * the archive page). Backend filter `muted_only=1` flips the mute query from
 * "exclude muted" to "only muted." Items here are NEVER counted in the bell
 * badge — they're, by definition, things the user told the app to hide.
 */
export const fetchMutedNotifications = (limit: number = 100) => wrap(async (dispatch: any) => {
    dispatch({ type: constants.NOTIFICATIONS_SET_MUTED_LOADING, payload: true });
    try {
        const params = new URLSearchParams();
        params.set('limit', String(limit));
        params.set('muted_only', '1');
        const res: any = await axiosCaller('GET', `notifications?${params.toString()}`);
        dispatch({
            type: constants.NOTIFICATIONS_SET_MUTED_ITEMS,
            payload: res?.data?.data || [],
        });
    } catch (e) {
        // Soft failure — Muted tab will show an empty state. Next manual
        // navigation to the tab retries.
    } finally {
        dispatch({ type: constants.NOTIFICATIONS_SET_MUTED_LOADING, payload: false });
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Sweep items from a now-unmuted source out of mutedItems immediately. The
 * next refetch will reconcile from the server, but this gives instant feedback.
 */
export const removeMutedItemsBySource = (entityType: string, entityId: number) => ({
    type: constants.NOTIFICATIONS_REMOVE_BY_SOURCE,
    payload: { entity_type: entityType, entity_id: entityId },
});

/**
 * Cheap polling endpoint that only returns the unread count. Use this on a
 * timer to keep the badge fresh without re-fetching the full list.
 */
export const fetchUnreadCount = () => wrap(async (dispatch: any) => {
    try {
        const res: any = await axiosCaller('GET', 'notifications/unread-count');
        dispatch({
            type: constants.NOTIFICATIONS_SET_UNREAD_COUNT,
            payload: res?.data?.unread_count ?? 0,
        });
    } catch (e) {
        // Silent — badge stays at last known value.
    }
}, 'NOTIFICATIONS_ERROR');

export const markNotificationRead = (id: number) => wrap(async (dispatch: any) => {
    // Optimistic: flip locally first, hit the API in the background.
    dispatch({ type: constants.NOTIFICATIONS_MARK_READ, payload: id });
    try {
        await axiosCaller('POST', `notifications/${id}/read`);
    } catch (e) {
        // Soft failure — UI state is already updated. Future poll will reconcile.
    }
}, 'NOTIFICATIONS_ERROR');

export const markAllNotificationsRead = () => wrap(async (dispatch: any) => {
    dispatch({ type: constants.NOTIFICATIONS_MARK_ALL_READ });
    try {
        await axiosCaller('POST', 'notifications/read-all');
    } catch (e) {
        // Soft failure — local state already updated, next poll will reconcile.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Toggle the starred flag locally first, then ask the server to do the same.
 * If the API call fails, we leave the local optimistic state in place — the
 * next refresh will reconcile. Avoids a flicker on the common-case happy path.
 */
export const toggleStarNotification = (id: number) => wrap(async (dispatch: any) => {
    dispatch({ type: constants.NOTIFICATIONS_TOGGLE_STAR, payload: id });
    try {
        await axiosCaller('POST', `notifications/${id}/star`);
    } catch (e) {
        // Soft failure — next list fetch reconciles.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Set or clear the snooze timestamp on a notification. `until` is an ISO
 * string when snoozing, or null to clear an existing snooze.
 */
export const snoozeNotification = (id: number, until: string | null) => wrap(async (dispatch: any) => {
    dispatch({ type: constants.NOTIFICATIONS_SET_SNOOZE, payload: { id, until } });
    try {
        await axiosCaller('POST', `notifications/${id}/snooze`, { until });
    } catch (e) {
        // Soft failure — next list fetch reconciles.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Bulk mark-as-read: optimistically flip every id locally, then fire one
 * request per id in parallel. The backend has no bulk endpoint yet — we
 * loop on the client. For typical inbox sizes (≤50 visible) this is fine;
 * if/when bulk endpoints land we'd swap this implementation only, callers
 * stay the same.
 */
export const bulkMarkRead = (ids: number[]) => wrap(async (dispatch: any) => {
    ids.forEach((id) => dispatch({ type: constants.NOTIFICATIONS_MARK_READ, payload: id }));
    try {
        await Promise.all(ids.map((id) => axiosCaller('POST', `notifications/${id}/read`)));
    } catch (e) {
        // Soft fail — next list fetch reconciles.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Flip the star on every id passed. The caller is expected to have already
 * filtered the selection so this list contains ONLY items that need to flip
 * (i.e. when "Star all" was the action, items already starred are excluded).
 * Keeps the network noise down + avoids the toggle-endpoint racing the user's
 * intent.
 */
export const bulkToggleStar = (ids: number[]) => wrap(async (dispatch: any) => {
    ids.forEach((id) => dispatch({ type: constants.NOTIFICATIONS_TOGGLE_STAR, payload: id }));
    try {
        await Promise.all(ids.map((id) => axiosCaller('POST', `notifications/${id}/star`)));
    } catch (e) {
        // Soft fail — next list fetch reconciles.
    }
}, 'NOTIFICATIONS_ERROR');

export const bulkSnooze = (ids: number[], until: string | null) => wrap(async (dispatch: any) => {
    ids.forEach((id) => dispatch({ type: constants.NOTIFICATIONS_SET_SNOOZE, payload: { id, until } }));
    try {
        await Promise.all(ids.map((id) => axiosCaller('POST', `notifications/${id}/snooze`, { until })));
    } catch (e) {
        // Soft fail — next list fetch reconciles.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Poll for notifications created after `sinceIso`. Used by the archive page
 * to stage "N new" items in a side slot rather than blasting them straight
 * into the list (which would shift the user's scroll + selection state).
 *
 * Also refreshes the unread badge — same response carries `unread_count`,
 * piggyback so we don't need a parallel call.
 *
 * Errors are swallowed: live-update polling is best-effort and must never
 * surface as a visible failure to the user.
 */
export const checkForNewNotifications = (sinceIso: string) => wrap(async (dispatch: any) => {
    try {
        const params = new URLSearchParams();
        params.set('since', sinceIso);
        params.set('limit', '50');
        const res: any = await axiosCaller('GET', `notifications?${params.toString()}`);
        const payload = res?.data || {};
        if (typeof payload.unread_count === 'number') {
            dispatch({ type: constants.NOTIFICATIONS_SET_UNREAD_COUNT, payload: payload.unread_count });
        }
        const data = payload.data || [];
        if (Array.isArray(data)) {
            dispatch({ type: constants.NOTIFICATIONS_SET_PENDING_NEW, payload: data });
        }
    } catch (e) {
        // Silent — the badge stays at last known value and pending list stays empty.
    }
}, 'NOTIFICATIONS_ERROR');

/**
 * Move the pending items into the main `items` list. Triggered by the user
 * clicking the "N new" pill. Synchronous — no API call needed.
 */
export const mergePendingNotifications = () => ({
    type: constants.NOTIFICATIONS_MERGE_PENDING,
});

/**
 * Receive a single notification from the WebSocket. Adds it to pendingNew
 * (so the pill shows) and bumps the unread badge. Synchronous — no API call.
 * If the item is already visible OR already pending, the reducer no-ops.
 */
export const receiveLiveNotification = (item: any) => (dispatch: any) => {
    if (!item || typeof item.id !== 'number') return;
    dispatch({ type: constants.NOTIFICATIONS_ADD_PENDING_NEW, payload: item });
    // Only bump if it would actually be "unread" in the user's inbox — the
    // backend sends read_at: null for fresh items, but be defensive.
    if (!item.read_at) {
        dispatch({ type: constants.NOTIFICATIONS_BUMP_UNREAD });
    }
};
